import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Chip,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  getProductBarcodeById,
  getActiveMaterialsByBarcodeId,
  getAllMaterialsByBarcodeId,
} from "../../../services/Export";
import { getUserType, getUser } from "../../../Utils/Common";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BuildIcon from "@mui/icons-material/Build";
import EditIcon from "@mui/icons-material/Edit";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

function ProductBarcodeView() {
  const theme = useTheme();
  const history = useHistory();
  const { barcodeId } = useParams();
  const [productBarcode, setProductBarcode] = useState(null);
  const [productionMaterials, setProductionMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching product barcode data for ID:", barcodeId);
      const result = await getProductBarcodeById(barcodeId);

      if (result && result.barcodeId) {
        setProductBarcode(result);

        // Fetch production materials (BOM)
        const materials = await getAllMaterialsByBarcodeId(result.barcodeId);
        if (materials && materials !== "Something went wrong...") {
          setProductionMaterials(materials);
        }
      }
    } catch (error) {
      console.error("Error fetching product barcode data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [barcodeId]);

  const isSalesPerson = getUserType() === "SALES_PERSON";
  const assignedWarehouseIds = isSalesPerson
    ? (getUser()?.wareHouseList || []).map((w) => w.warehouseId)
    : null;

  const displayedStocks = isSalesPerson
    ? (productBarcode?.warehouseStocks || []).filter((s) =>
        assignedWarehouseIds.includes(s.warehouseId),
      )
    : productBarcode?.warehouseStocks || [];

  const displayedTotalQty = isSalesPerson
    ? displayedStocks.reduce((sum, s) => sum + (s.stockQty || 0), 0)
    : productBarcode?.totalQtyAllWarehouses || 0;

  const displayedWarehouseCount = isSalesPerson
    ? displayedStocks.length
    : productBarcode?.numberOfWarehouses || 0;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading product details...
        </Typography>
      </Box>
    );
  }

  if (!productBarcode || !productBarcode.barcodeId) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No product data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 3 },
        borderRadius: 2,
        bgcolor: theme.palette.mode === "dark"
          ? theme.palette.background.paper
          : "#f9fafb",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ShoppingBasketIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary.main">
              Product Details (Master)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Viewing product across all warehouses
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Grid container spacing={2}>
        {/* Product Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <InventoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Product Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Product Name
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {productBarcode.productName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Barcode
                  </Typography>
                  <Typography variant="body1">
                    {productBarcode.barcode}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Typography variant="body1">
                    {productBarcode.addedBy}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {productBarcode.description || "No description"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AssessmentIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Inventory Statistics
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {isSalesPerson
                        ? "Total Qty (Assigned Warehouses)"
                        : "Total Qty (All Warehouses)"}
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="600"
                      color="primary.main"
                    >
                      {displayedTotalQty}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Number of Warehouses
                    </Typography>
                    <Typography variant="h5" fontWeight="600" color="info.main">
                      {displayedWarehouseCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      BOM Version
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="600"
                      color="warning.main"
                    >
                      v{productBarcode.currentMaterialsVersion || 1}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Categories */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Product Categories
                </Typography>
              </Box>

              {productBarcode?.categories?.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {productBarcode.categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography variant="body2">
                          <span style={{ fontWeight: "600" }}>
                            {category.category}
                          </span>
                          : {category.subCat_1}
                        </Typography>
                      }
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: 1,
                        py: 0.5,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No categories available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Production Materials (BOM) */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <BuildIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Production Materials (BOM)
                  </Typography>
                </Box>
                <RoleBasedRender
                  allowedRoles={[
                    "MATERIALS_FOR_PRODUCT_VIEW",
                    "MATERIALS_FOR_PRODUCT_UPDATE",
                    "MATERIALS_FOR_PRODUCT_CREATE",
                    "MATERIALS_FOR_PRODUCT_DELETE",
                    "ROLE_ADMIN",
                  ]}
                >
                  <Tooltip title="Manage Materials">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() =>
                        history.push(`/template/product/materials/${barcodeId}`)
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </RoleBasedRender>
              </Box>

              {productionMaterials?.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {productionMaterials.map((material, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography variant="body2">
                          <span style={{ fontWeight: "600" }}>
                            {material.materialName}
                          </span>
                          : {material.standardQtyPerUnit}{" "}
                          {material.materialUnitType || "units"} per unit
                        </Typography>
                      }
                      sx={{
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        borderRadius: 1,
                        py: 0.5,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    No materials defined yet
                  </Typography>
                  <RoleBasedRender
                    allowedRoles={[
                      "MATERIALS_FOR_PRODUCT_VIEW",
                      "MATERIALS_FOR_PRODUCT_UPDATE",
                      "MATERIALS_FOR_PRODUCT_CREATE",
                      "MATERIALS_FOR_PRODUCT_DELETE",
                      "ROLE_ADMIN",
                    ]}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<BuildIcon />}
                      onClick={() =>
                        history.push(`/template/product/materials/${barcodeId}`)
                      }
                    >
                      Add Materials (BOM)
                    </Button>
                  </RoleBasedRender>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Warehouse Stock Distribution */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <WarehouseIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Warehouse Stock Distribution
                </Typography>
              </Box>

              {displayedStocks.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Warehouse Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Type</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Stock Qty</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedStocks.map((stock, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          <TableCell>{stock.warehouseName}</TableCell>
                          <TableCell>
                            <Chip
                              label={stock.warehouseType}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={stock.stockQty}
                              color={stock.stockQty > 0 ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={stock.status}
                              color={
                                stock.status === "ACTIVE"
                                  ? "success"
                                  : "default"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                              }}
                            >
                              <Tooltip title="View Warehouse Details">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    history.push(
                                      `/template/warehouse-product/${stock.productId}`,
                                    );
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {stock.warehouseName === "Main Factory" && (
                                <RoleBasedRender
                                  allowedRoles={[
                                    "PRODUCTION_CREATE",
                                    "ROLE_ADMIN",
                                  ]}
                                >
                                  <Tooltip title="Add Production">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => {
                                        history.push(
                                          `/template/production/${stock.productId}`,
                                        );
                                      }}
                                    >
                                      <BuildIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </RoleBasedRender>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">
                  No warehouse stock data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ProductBarcodeView;
